import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ClipboardList, Plus, Trash2 } from 'lucide-react';

import { listDepartments, listEmployees } from '@/api/employees.api';
import {
  createAssignment,
  deactivateAssignment,
  listAssignments,
  listCourses,
  type AssignmentTargetType,
  type TrainingAssignment,
} from '@/api/trainingAdmin.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { describeTrainingError } from '@/lib/training/errors';
import { SYSTEM_ROLES, type SystemRole } from '@/types';
import { useToast } from '@/hooks/useToast';
import { TrainingNoAccessCard } from './_chrome';
import { useTrainingAdminGate } from './_gate';

/**
 * Assignment rules (manage_training / superadmin) — who gets which
 * course, with due windows and re-certification intervals. Creating a
 * rule materializes immediately: the response reports how many people
 * were enrolled. Deactivation is soft — existing enrollments stay.
 */

const SELECT_CLS =
  'w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-2 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

const TARGET_TYPE_LABEL: Record<AssignmentTargetType, string> = {
  all: 'Everyone',
  membership: 'One member',
  department: 'Department',
  role: 'Role',
};

/** Auditor seats are excluded from expansion server-side — don't offer them. */
const ASSIGNABLE_ROLES: SystemRole[] = SYSTEM_ROLES.filter((r) => r !== 'auditor_readonly');

export function AssignmentsPage() {
  const { canManage } = useTrainingAdminGate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [deactivating, setDeactivating] = useState<TrainingAssignment | null>(null);

  const assignmentsQuery = useQuery({
    queryKey: ['training-admin', 'assignments'],
    queryFn: listAssignments,
    enabled: canManage,
  });
  const departmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
    enabled: canManage,
  });
  const employeesQuery = useQuery({
    queryKey: ['employees', 'for-training-admin'],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
    enabled: canManage,
  });

  const departmentName = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of departmentsQuery.data ?? []) map.set(d.id, d.name);
    return map;
  }, [departmentsQuery.data]);

  const memberName = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of employeesQuery.data?.data ?? []) {
      if (e.membershipId) map.set(e.membershipId, `${e.firstName} ${e.lastName}`);
    }
    return map;
  }, [employeesQuery.data]);

  if (!canManage) {
    return <TrainingNoAccessCard capability="manage_training" />;
  }

  function targetLabel(a: TrainingAssignment): string {
    switch (a.targetType) {
      case 'all':
        return 'Everyone';
      case 'membership':
        return a.targetMembershipId
          ? (memberName.get(a.targetMembershipId) ?? 'Unknown member')
          : 'Unknown member';
      case 'department':
        return a.targetDepartmentId
          ? (departmentName.get(a.targetDepartmentId) ?? 'Unknown department')
          : 'Unknown department';
      case 'role':
        return a.targetRole ? `Role: ${a.targetRole.replace(/_/g, ' ')}` : 'Unknown role';
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">Training assignments</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            Rules that enroll people in courses — new hires and department moves pick them up automatically.
          </p>
        </div>
        <Button
          onClick={() => {
            setCreateOpen(true);
          }}
        >
          <Plus className="size-4" />
          New assignment
        </Button>
      </div>

      {assignmentsQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : assignmentsQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <p className="text-[13px] text-kw-danger-text">
            {describeTrainingError(assignmentsQuery.error, 'Could not load assignments.')}
          </p>
        </Card>
      ) : (assignmentsQuery.data ?? []).length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <ClipboardList className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No assignment rules yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Create one to enroll people in a published course.
            </p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="min-w-full text-left text-[13px]">
            <thead>
              <tr className="border-b border-kw-border text-[12px] uppercase tracking-wide text-kw-muted">
                <th className="px-4 py-2.5 font-medium">Course</th>
                <th className="px-4 py-2.5 font-medium">Target</th>
                <th className="px-4 py-2.5 font-medium">Due</th>
                <th className="px-4 py-2.5 font-medium">Recert</th>
                <th className="px-4 py-2.5 font-medium">Enrolled</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-kw-border">
              {(assignmentsQuery.data ?? []).map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-3 font-medium text-kw-shark">{a.course.title}</td>
                  <td className="px-4 py-3 text-kw-shark">{targetLabel(a)}</td>
                  <td className="px-4 py-3 text-kw-shark">
                    {a.dueInDays !== null ? `${String(a.dueInDays)} days` : '—'}
                  </td>
                  <td className="px-4 py-3 text-kw-shark">
                    {a.recertIntervalDays !== null
                      ? `Every ${String(a.recertIntervalDays)} days`
                      : 'One-time'}
                  </td>
                  <td className="px-4 py-3 text-kw-shark">{a._count?.enrollments ?? '—'}</td>
                  <td className="px-4 py-3">
                    {a.isActive ? (
                      <Badge variant="success">Active</Badge>
                    ) : (
                      <Badge variant="neutral">Inactive</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {a.isActive ? (
                      <button
                        type="button"
                        aria-label={`Deactivate assignment for ${a.course.title}`}
                        onClick={() => {
                          setDeactivating(a);
                        }}
                        className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {createOpen ? (
        <CreateAssignmentModal
          onClose={() => {
            setCreateOpen(false);
          }}
          onSaved={(enrolled) => {
            void queryClient.invalidateQueries({ queryKey: ['training-admin', 'assignments'] });
            toast.show(
              `Assignment created — ${String(enrolled)} ${enrolled === 1 ? 'person' : 'people'} enrolled.`,
              { variant: 'success' },
            );
          }}
        />
      ) : null}

      {deactivating ? (
        <DeactivateModal
          assignment={deactivating}
          onClose={() => {
            setDeactivating(null);
          }}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['training-admin', 'assignments'] });
            toast.show('Assignment deactivated — existing enrollments stay.', { variant: 'success' });
          }}
        />
      ) : null}
    </div>
  );
}

function CreateAssignmentModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (enrollmentsCreated: number) => void;
}) {
  const [courseId, setCourseId] = useState('');
  const [targetType, setTargetType] = useState<AssignmentTargetType>('all');
  const [targetMembershipId, setTargetMembershipId] = useState('');
  const [targetDepartmentId, setTargetDepartmentId] = useState('');
  const [targetRole, setTargetRole] = useState<SystemRole>('employee');
  const [dueInDays, setDueInDays] = useState('');
  const [recertDays, setRecertDays] = useState('');
  const [error, setError] = useState<string | null>(null);

  const coursesQuery = useQuery({ queryKey: ['training-admin', 'courses'], queryFn: listCourses });
  const departmentsQuery = useQuery({ queryKey: ['departments'], queryFn: listDepartments });
  const employeesQuery = useQuery({
    queryKey: ['employees', 'for-training-admin'],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
  });

  const members = (employeesQuery.data?.data ?? []).filter((e) => e.membershipId !== null);
  const publishedCourses = (coursesQuery.data ?? []).filter((c) => c.state === 'published');

  const mutation = useMutation({
    mutationFn: () =>
      createAssignment({
        courseId,
        targetType,
        ...(targetType === 'membership' ? { targetMembershipId } : {}),
        ...(targetType === 'department' ? { targetDepartmentId } : {}),
        ...(targetType === 'role' ? { targetRole } : {}),
        dueInDays: dueInDays.trim() === '' ? null : Number(dueInDays),
        recertIntervalDays: recertDays.trim() === '' ? null : Number(recertDays),
      }),
    onSuccess: (result) => {
      onSaved(result.enrollmentsCreated);
      onClose();
    },
    onError: (err) => {
      setError(describeTrainingError(err, 'Could not create the assignment.'));
    },
  });

  const valid =
    courseId !== '' &&
    (targetType !== 'membership' || targetMembershipId !== '') &&
    (targetType !== 'department' || targetDepartmentId !== '');

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!valid) return;
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title="New assignment" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Course</span>
          <select
            value={courseId}
            onChange={(e) => {
              setCourseId(e.target.value);
            }}
            className={SELECT_CLS}
          >
            <option value="">Select a published course…</option>
            {publishedCourses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} (v{c.version})
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Assign to</span>
          <select
            value={targetType}
            onChange={(e) => {
              const v = e.target.value;
              setTargetType(v === 'membership' || v === 'department' || v === 'role' ? v : 'all');
            }}
            className={SELECT_CLS}
          >
            {(Object.keys(TARGET_TYPE_LABEL) as AssignmentTargetType[]).map((t) => (
              <option key={t} value={t}>
                {TARGET_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>

        {targetType === 'membership' ? (
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Member</span>
            <select
              value={targetMembershipId}
              onChange={(e) => {
                setTargetMembershipId(e.target.value);
              }}
              className={SELECT_CLS}
            >
              <option value="">Select a member…</option>
              {members.map((m) => (
                <option key={m.id} value={m.membershipId ?? ''}>
                  {m.firstName} {m.lastName}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {targetType === 'department' ? (
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Department</span>
            <select
              value={targetDepartmentId}
              onChange={(e) => {
                setTargetDepartmentId(e.target.value);
              }}
              className={SELECT_CLS}
            >
              <option value="">Select a department…</option>
              {(departmentsQuery.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {targetType === 'role' ? (
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Role</span>
            <select
              value={targetRole}
              onChange={(e) => {
                const v = e.target.value;
                setTargetRole(ASSIGNABLE_ROLES.includes(v as SystemRole) ? (v as SystemRole) : 'employee');
              }}
              className={SELECT_CLS}
            >
              {ASSIGNABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="Due in (days)"
            name="assignment-due-days"
            type="number"
            min={0}
            max={3650}
            placeholder="No due date"
            value={dueInDays}
            onChange={(e) => {
              setDueInDays(e.target.value);
            }}
          />
          <Input
            label="Recert every (days)"
            name="assignment-recert-days"
            type="number"
            min={1}
            max={3650}
            placeholder="One-time"
            value={recertDays}
            onChange={(e) => {
              setRecertDays(e.target.value);
            }}
            hint="e.g. 365 for annual re-certification"
          />
        </div>

        {error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">{error}</p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!valid}>
            Create &amp; enroll
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DeactivateModal({
  assignment,
  onClose,
  onDone,
}: {
  assignment: TrainingAssignment;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => deactivateAssignment(assignment.id, reason.trim()),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (err) => {
      setError(describeTrainingError(err, 'Could not deactivate the assignment.'));
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!reason.trim()) return;
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title="Deactivate assignment" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-[13px] text-kw-shark">
          The rule for <span className="font-medium">{assignment.course.title}</span> stops enrolling new
          people and stops opening re-certification cycles. Existing enrollments are kept — training history
          is append-only.
        </p>
        <Input
          label="Reason for change (audit log)"
          name="assignment-deactivate-reason"
          required
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          placeholder="e.g. Course replaced by GMP Fundamentals v2"
        />
        {error ? <p className="text-[12px] text-kw-danger-text">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" isLoading={mutation.isPending} disabled={!reason.trim()}>
            Deactivate
          </Button>
        </div>
      </form>
    </Modal>
  );
}
